<template>
  <!--
    매장 하나의 **우리동네TV 쿠폰** 패널(R-C) — 매장 상세 세 화면(사업장 트리 · 매장 관리 · 매장 정보 관리)이 같은 이
    패널을 탭으로 쓴다. 어휘 → 문구·톤·버튼은 전부 `utils/tvCoupon` 한 곳이고, 이 패널은 조회·요청·그리기만 한다.

    권한은 세션 **액션**만 본다(역할 이름 없음). 탭 자체는 목록 액션(`TvCouponList`)이 있을 때만 그려진다(화면 쪽 `v-if`).
    쓰기 실패의 문장은 인터셉터가 **서버 문장 그대로** 띄운다 — 이 패널은 같은 말을 한 번 더 하지 않는다.
  -->
  <div class="tv-coupon-panel">
    <el-alert v-if="!storeCd" :title="$t(text.noStore)" type="info" :closable="false" />

    <template v-else>
      <div class="tv-coupon-panel__head">
        <h4>{{ $t(text.panelTitle) }}</h4>
        <div class="tv-coupon-panel__tools">
          <el-button size="mini" icon="el-icon-refresh" :disabled="loading || busy" @click="load">{{
            $t(text.refreshAction)
          }}</el-button>
          <el-button
            v-if="canPublish"
            class="tv-coupon-panel__publish"
            size="mini"
            type="primary"
            icon="el-icon-plus"
            :disabled="!eligibility.publishable || busy || loading"
            @click="openPublish"
            >{{ $t(text.publishAction) }}</el-button
          >
        </div>
      </div>

      <!-- 발행을 막는 이유 — 버튼만 꺼 두면 「왜」가 없다. 서버가 준 사유를 모두 한 줄씩 -->
      <el-alert
        v-if="canPublish && eligibility.reasonLines.length > 0"
        class="tv-coupon-panel__blocked"
        type="warning"
        :title="$t(text.blockedTitle)"
        :closable="false"
        show-icon>
        <ul class="tv-coupon-panel__reasons">
          <li v-for="(line, index) in eligibility.reasonLines" :key="index">{{ $t(line) }}</li>
        </ul>
      </el-alert>

      <!-- 조회 실패는 「쿠폰 없음」이 아니다 — 실패라고 말하고 다시 시도할 자리를 준다 -->
      <el-alert v-if="loadFailed" :title="$t(text.loadFailed)" type="error" :closable="false" show-icon>
        <el-button size="mini" type="text" @click="load">{{ $t(text.retryAction) }}</el-button>
      </el-alert>

      <el-table v-else :data="viewRows" size="small" border v-loading="loading" :empty-text="$t(text.empty)">
        <el-table-column :label="$t(text.titleColumn)" min-width="200">
          <template slot-scope="scope">
            <div class="tv-coupon-panel__title">{{ scope.row.row.title }}</div>
            <div class="tv-coupon-panel__muted">{{ scope.row.discountText }}</div>
          </template>
        </el-table-column>

        <el-table-column :label="$t(text.statusColumn)" width="100" align="center">
          <template slot-scope="scope">
            <el-tooltip placement="top" :disabled="!scope.row.badge.note" :content="scope.row.badge.note || ''">
              <el-tag size="mini" :type="scope.row.badge.type" class="tv-coupon-panel__status">{{
                $t(scope.row.badge.label)
              }}</el-tag>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column :label="$t(text.periodColumn)" width="190" align="center">
          <template slot-scope="scope">{{ scope.row.periodText }}</template>
        </el-table-column>

        <el-table-column :label="$t(text.claimColumn)" width="80" align="right">
          <template slot-scope="scope">{{ scope.row.claimText }}</template>
        </el-table-column>

        <el-table-column :label="$t(text.ktColumn)" width="150">
          <template slot-scope="scope">
            <el-tooltip v-if="scope.row.ktChip" placement="top" :disabled="scope.row.ktChip.lines.length === 0">
              <div slot="content" class="tv-coupon-panel__tip">
                <div v-for="(line, index) in scope.row.ktChip.lines" :key="index">{{ line }}</div>
              </div>
              <el-tag size="mini" :type="scope.row.ktChip.tagType" class="tv-coupon-panel__kt">{{
                scope.row.ktChip.text
              }}</el-tag>
            </el-tooltip>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column :label="$t(text.landingColumn)" min-width="260">
          <template slot-scope="scope">
            <span class="tv-coupon-panel__url">{{ scope.row.row.landingUrl }}</span>
            <el-button size="mini" type="text" icon="el-icon-copy-document" @click="copy(scope.row.row.landingUrl)">{{
              $t(text.copyAction)
            }}</el-button>
          </template>
        </el-table-column>

        <!-- 버튼 셋(수정·중지·재전송)이 한 줄에 들어가는 폭 — 가로로 넘치는 표에서 할 일이 스크롤 뒤에 숨지 않게 오른쪽 고정 -->
        <el-table-column :label="$t(text.manageColumn)" width="250" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.actions.edit" size="mini" :disabled="busy" @click="openEdit(scope.row.row)">{{
              $t(text.editAction)
            }}</el-button>
            <el-button
              v-if="scope.row.actions.stop"
              size="mini"
              type="danger"
              plain
              :disabled="busy"
              @click="confirmStop(scope.row.row)"
              >{{ $t(text.stopAction) }}</el-button
            >
            <el-button
              v-if="scope.row.actions.resend"
              size="mini"
              type="warning"
              plain
              :disabled="busy"
              @click="resend(scope.row.row)"
              >{{ $t(text.resendAction) }}</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <ul class="tv-coupon-panel__notes">
        <li v-for="(line, index) in text.notes" :key="index">{{ $t(line) }}</li>
      </ul>
    </template>

    <!-- 발행·수정·발행 결과 — 매장 관리 다이얼로그(fullscreen) 안에서도 뜨도록 body 에 붙인다 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialog.visible"
      width="600px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="tv-coupon-dialog">
      <template v-if="dialog.result">
        <el-alert type="success" :title="$t(text.resultHead)" :closable="false" show-icon />
        <dl class="tv-coupon-dialog__result">
          <dt>{{ $t(text.titleLabel) }}</dt>
          <dd>{{ dialog.result.title }}</dd>
          <dt>{{ $t(text.periodLabel) }}</dt>
          <dd>{{ periodText(dialog.result) }}</dd>
          <dt>{{ $t(text.landingLabel) }}</dt>
          <dd>
            <span class="tv-coupon-panel__url">{{ dialog.result.landingUrl }}</span>
            <el-button size="mini" type="text" icon="el-icon-copy-document" @click="copy(dialog.result.landingUrl)">{{
              $t(text.copyAction)
            }}</el-button>
          </dd>
        </dl>
      </template>

      <el-form v-else label-width="100px" size="small" @submit.native.prevent>
        <el-form-item :label="$t(text.discountLabel)">
          <el-select
            v-if="dialog.publishing"
            v-model="dialog.draft.discountCd"
            class="tv-coupon-dialog__discount"
            :placeholder="$t(text.discountPlaceholder)">
            <el-option
              v-for="discount in eligibility.discounts"
              :key="discount.discountCd"
              :label="discountLabel(discount)"
              :value="discount.discountCd" />
          </el-select>
          <span v-else>{{ rowDiscountText(dialog.row) }}</span>
          <p v-if="dialog.publishing" class="tv-coupon-dialog__hint tv-coupon-dialog__rule">
            {{ $t(text.discountRule) }}
          </p>
          <p v-if="discountWarning" class="tv-coupon-dialog__warn">{{ $t(discountWarning) }}</p>
        </el-form-item>

        <el-form-item :label="$t(text.titleLabel)">
          <el-input
            v-model="dialog.draft.title"
            class="tv-coupon-dialog__title"
            :maxlength="titleMax"
            show-word-limit
            :placeholder="titlePlaceholder" />
          <p class="tv-coupon-dialog__hint">
            {{ $t(dialog.publishing ? text.titlePublishHint : text.titleEditHint) }}
          </p>
        </el-form-item>

        <el-form-item :label="$t(text.periodLabel)">
          <el-date-picker
            v-model="dialog.draft.period"
            class="tv-coupon-dialog__period"
            type="daterange"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            range-separator="~"
            :start-placeholder="$t(text.periodStart)"
            :end-placeholder="$t(text.periodEnd)" />
        </el-form-item>

        <el-form-item :label="$t(text.landingLabel)">
          <template v-if="dialog.row">
            <span class="tv-coupon-panel__url">{{ dialog.row.landingUrl }}</span>
            <el-button size="mini" type="text" icon="el-icon-copy-document" @click="copy(dialog.row.landingUrl)">{{
              $t(text.copyAction)
            }}</el-button>
          </template>
          <span v-else class="tv-coupon-dialog__hint">{{ $t(text.landingPending) }}</span>
        </el-form-item>

        <el-alert
          v-if="dialog.problem"
          class="tv-coupon-dialog__problem"
          type="error"
          :title="dialog.problem"
          :closable="false"
          show-icon />
      </el-form>

      <span slot="footer">
        <template v-if="dialog.result">
          <el-button size="small" type="primary" @click="dialog.visible = false">{{ $t(text.closeAction) }}</el-button>
        </template>
        <template v-else>
          <el-button size="small" @click="dialog.visible = false">{{ $t(text.cancelAction) }}</el-button>
          <el-button
            size="small"
            type="primary"
            class="tv-coupon-dialog__submit"
            :loading="busy"
            :disabled="busy"
            @click="submitDialog"
            >{{ $t(dialog.publishing ? text.publishAction : text.saveAction) }}</el-button
          >
        </template>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { httpStatusOf } from '@/plugins/http/policy'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'
import { copyText } from '@/utils/clipboard'
import { latestGuard } from '@/utils/latestGuard'
import {
  TV_COUPON_ACTION,
  TV_COUPON_API,
  TV_COUPON_TEXT,
  TV_COUPON_TITLE_MAX,
  tvCouponClaimText,
  tvCouponDiscountLabel,
  tvCouponDiscountWarning,
  tvCouponDraft,
  tvCouponDraftProblem,
  tvCouponEligibilityView,
  tvCouponKtChip,
  tvCouponPeriodText,
  tvCouponPublishBody,
  tvCouponReviseBody,
  tvCouponRowActions,
  tvCouponRowDiscountText,
  tvCouponStatusBadge,
  tvCouponStopConfirmMessage,
  tvCouponUrl,
  tvCouponWriteFailure,
} from '@/utils/tvCoupon'

const closedDialog = () => ({
  visible: false,
  publishing: false,
  row: null,
  draft: tvCouponDraft(null),
  problem: '',
  result: null,
})

export default {
  name: 'TvCouponPanel',

  props: {
    storeCd: { type: Number, default: null },
    /** 부모가 올리면 같은 매장을 다시 읽는다 — 매장 저장 뒤(사업자번호가 바뀌면 발행 자격이 바뀐다) */
    reloadSeq: { type: Number, default: 0 },
  },

  data() {
    return {
      text: TV_COUPON_TEXT,
      titleMax: TV_COUPON_TITLE_MAX,
      loading: false,
      busy: false,
      loadFailed: false,
      /** 서버 목록 행 그대로 — 표시는 [viewRows] 가 파생한다 */
      rows: [],
      /** eligibility 응답 그대로(못 읽었거나 부르지 않았으면 `null`) */
      eligibilityData: null,
      dialog: closedDialog(),
      /** 매장이 바뀐 뒤 도착한 앞 매장의 응답이 지금 매장을 덮지 않게(`utils/latestGuard`) */
      guard: latestGuard(),
    }
  },

  computed: {
    /** 발행은 발행 액션과 자격 조회 액션이 **둘 다** 있어야 한다 — 자격을 모르면 버튼을 켤 근거가 없다 */
    canPublish() {
      return checkAuthActionAccess(TV_COUPON_ACTION.PUBLISH) && checkAuthActionAccess(TV_COUPON_ACTION.ELIGIBILITY)
    },
    can() {
      return {
        update: checkAuthActionAccess(TV_COUPON_ACTION.UPDATE),
        stop: checkAuthActionAccess(TV_COUPON_ACTION.STOP),
        resend: checkAuthActionAccess(TV_COUPON_ACTION.RESEND),
      }
    },
    eligibility() {
      return tvCouponEligibilityView(this.eligibilityData)
    },
    viewRows() {
      return this.rows.map((row) => ({
        row,
        badge: tvCouponStatusBadge(row.status),
        ktChip: tvCouponKtChip(row.kt),
        actions: tvCouponRowActions(row, this.can),
        periodText: tvCouponPeriodText(row),
        discountText: tvCouponRowDiscountText(row),
        claimText: tvCouponClaimText(row),
      }))
    },
    dialogTitle() {
      if (this.dialog.result) return this.$t(this.text.resultDialogTitle)

      return this.$t(this.dialog.publishing ? this.text.publishDialogTitle : this.text.editDialogTitle)
    },
    selectedDiscount() {
      const cd = this.dialog.draft.discountCd

      return this.eligibility.discounts.find((discount) => discount.discountCd === cd) || null
    },
    discountWarning() {
      return this.dialog.publishing ? tvCouponDiscountWarning(this.selectedDiscount) : null
    },
    /** 발행은 비우면 쓰일 할인 이름을, 수정은 지금 제목을 흐리게 보인다 */
    titlePlaceholder() {
      if (!this.dialog.publishing) return (this.dialog.row && this.dialog.row.title) || ''

      return (this.selectedDiscount && this.selectedDiscount.discountNm) || ''
    },
  },

  watch: {
    storeCd: {
      immediate: true,
      handler(val) {
        this.guard.invalidate() // 앞 매장의 응답은 버린다
        this.rows = []
        this.eligibilityData = null
        this.loadFailed = false
        this.dialog = closedDialog()
        if (val) this.load()
      },
    },
    reloadSeq() {
      if (this.storeCd) this.load()
    },
  },

  methods: {
    discountLabel: tvCouponDiscountLabel,
    rowDiscountText: tvCouponRowDiscountText,
    periodText: tvCouponPeriodText,

    /** 목록과 발행 자격을 함께 읽는다 — 쓰기 뒤에도 이 한 곳으로 다시 읽는다(상태·전송 칩·자격이 함께 바뀐다) */
    async load() {
      const token = this.guard.begin()
      const params = { params: { storeCd: this.storeCd } }

      this.loading = true
      try {
        const [list, eligibility] = await Promise.all([
          this.$axios.get(TV_COUPON_API, params),
          this.canPublish ? this.$axios.get(`${TV_COUPON_API}/eligibility`, params) : Promise.resolve(null),
        ])

        if (!this.guard.isLatest(token)) return

        this.rows = Array.isArray(list && list.data) ? list.data : []
        this.eligibilityData = (eligibility && eligibility.data) || null
        this.loadFailed = false
      } catch (error) {
        // 인터셉터가 이미 서버 문장을 띄웠다 — 여기서는 실패 상태만 그린다
        if (!this.guard.isLatest(token)) return

        this.rows = []
        this.eligibilityData = null
        this.loadFailed = true
      } finally {
        if (this.guard.isLatest(token)) this.loading = false
      }
    },

    /**
     * 쓰기 한 곳 — 성공하면 응답 행을 돌려주고 목록을 다시 읽는다. 실패는 `null` 이고, 화면이 낡았다는 실패(404·409)면
     * 다시 읽는다([tvCouponWriteFailure]). 매장이 그 사이 바뀌었으면 응답을 버린다.
     *
     * @returns {Promise<?{row: ?object, stale: boolean}>} `stale` — 실패가 화면이 낡았다는 뜻이라 다시 읽었다
     */
    async write(request) {
      if (this.busy) return null

      const storeCd = this.storeCd

      this.busy = true
      try {
        const response = await request()

        if (this.storeCd !== storeCd) return null

        this.load()

        return { row: (response && response.data) || null, stale: false }
      } catch (error) {
        if (this.storeCd !== storeCd) return null

        const stale = tvCouponWriteFailure(httpStatusOf(error)) === 'RELOAD'
        if (stale) this.load()

        return { row: null, stale }
      } finally {
        this.busy = false
      }
    },

    openPublish() {
      this.dialog = { ...closedDialog(), visible: true, publishing: true }
    },

    openEdit(row) {
      this.dialog = { ...closedDialog(), visible: true, row, draft: tvCouponDraft(row) }
    },

    async submitDialog() {
      const { publishing, row, draft } = this.dialog
      const problem = tvCouponDraftProblem(draft, publishing)

      this.dialog.problem = problem ? this.$t(problem) : ''
      if (problem) return

      const result = await this.write(() =>
        publishing
          ? this.$axios.post(TV_COUPON_API, tvCouponPublishBody(this.storeCd, draft))
          : this.$axios.put(tvCouponUrl(row.tvCouponCd), tvCouponReviseBody(draft))
      )

      if (!result) return
      if (result.row && publishing) {
        // 발행 결과 — 랜딩 주소는 서버가 정한 값이다(QR 로 구워지므로 여기서 보여 주고 복사하게 한다)
        this.dialog.result = result.row
        return
      }
      if (result.row) {
        this.dialog.visible = false
        this.$message.success(this.$t(this.text.saved))
        return
      }
      // 실패 — 화면이 낡았으면(그 사이 중지·게시 끝) 다이얼로그를 닫고 새 목록을 본다. 아니면 고쳐서 다시 보내게 둔다
      if (result.stale) this.dialog.visible = false
    },

    confirmStop(row) {
      this.$confirm(tvCouponStopConfirmMessage(row), this.$t(this.text.stopConfirmTitle), {
        confirmButtonText: this.$t(this.text.stopConfirmButton),
        cancelButtonText: this.$t(this.text.cancelAction),
        type: 'warning',
      })
        .then(async () => {
          const result = await this.write(() => this.$axios.put(tvCouponUrl(row.tvCouponCd, 'stop')))
          if (result && result.row) this.$message.success(this.$t(this.text.stopped))
        })
        .catch(() => {})
    },

    async resend(row) {
      const result = await this.write(() => this.$axios.post(tvCouponUrl(row.tvCouponCd, 'resend')))
      if (result && result.row) this.$message.success(this.$t(this.text.resent))
    },

    copy(value) {
      return copyText(value).then((copied) =>
        copied ? this.$message.success(this.$t(this.text.copied)) : this.$message.warning(this.$t(this.text.copyFailed))
      )
    },
  },
}
</script>

<style scoped>
.tv-coupon-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 8px;
}

.tv-coupon-panel__head h4 {
  margin: 0;
}

.tv-coupon-panel__tools .el-button + .el-button {
  margin-left: 6px;
}

.tv-coupon-panel__blocked {
  margin-bottom: 10px;
}

.tv-coupon-panel__reasons {
  margin: 4px 0 0;
  padding-left: 18px;
  line-height: 1.7;
}

.tv-coupon-panel__title {
  font-weight: 600;
}

.tv-coupon-panel__muted {
  color: #909399;
  font-size: 12px;
}

.tv-coupon-panel__url {
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
  margin-right: 4px;
}

.tv-coupon-panel__tip {
  line-height: 1.6;
  max-width: 300px;
}

.tv-coupon-panel__notes {
  color: #909399;
  font-size: 12px;
  line-height: 1.8;
  margin: 12px 0 0;
  padding-left: 18px;
}

.tv-coupon-dialog__discount,
.tv-coupon-dialog__period {
  width: 100%;
}

.tv-coupon-dialog__hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
  margin: 4px 0 0;
}

.tv-coupon-dialog__warn {
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.6;
  margin: 4px 0 0;
}

.tv-coupon-dialog__result {
  margin: 14px 0 0;
  line-height: 1.8;
}

.tv-coupon-dialog__result dt {
  color: #909399;
  font-size: 12px;
}

.tv-coupon-dialog__result dd {
  margin: 0 0 8px;
}
</style>
